/**
 * Un deploy nuevo borra los archivos con hash de la versión anterior. Una pestaña con el shell viejo
 * pide un chunk que ya no existe y la ruta falla. Aquí se detecta ese fallo y se recupera solo: se
 * quitan el service worker y el shell guardado y se recarga una vez. Solo si la red responde de
 * verdad: con mala señal el fallo es de la conexión, y las cachés son lo único que abre la app.
 */

const RELOAD_KEY = 'parche:stale-chunk-reload';
/** Una segunda falla dentro de esta ventana ya no recarga (evita un bucle) y muestra el error. */
const RELOAD_WINDOW_MS = 30_000;
/** Cuánto se espera al servidor para comprobar que hay red antes de tocar las cachés. */
const PROBE_TIMEOUT_MS = 4000;

/** Mensajes de un `import()` que no pudo descargar el módulo (Chrome, Firefox y Safari). */
const CHUNK_ERROR = /dynamically imported module|Importing a module script failed|Unable to preload CSS|error loading dynamically/i;

export function isChunkLoadError(error: unknown): boolean {
  return error instanceof Error && CHUNK_ERROR.test(error.message);
}

/** `navigator.onLine` miente con Wi-Fi sin internet: se pide el shell al servidor, sin caché. */
async function networkReachable(): Promise<boolean> {
  if (navigator.onLine === false) return false;
  try {
    const response = await fetch('/index.html', { cache: 'no-store', signal: AbortSignal.timeout(PROBE_TIMEOUT_MS) });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Quita el service worker y el shell guardado: la siguiente carga viene de la red. Las respuestas del
 * API guardadas (`parche-api-`) y los archivos con hash (`parche-runtime-`) se conservan.
 */
export async function clearAppCaches(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
    if (typeof caches !== 'undefined') {
      const names = await caches.keys();
      await Promise.all(names.filter((name) => name.startsWith('parche-shell-')).map((name) => caches.delete(name)));
    }
  } catch {
    // Sin permiso para tocar cachés: igual se recarga
  }
}

/** Recarga; con red comprobada, primero quita el service worker y el shell guardado. */
export async function hardReload(): Promise<void> {
  if (await networkReachable()) await clearAppCaches();
  window.location.reload();
}

/** Recuerda el intento y comprueba que quedó guardado (un WebView puede aceptarlo sin conservarlo). */
function markReload(now: number): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY));
    if (Number.isFinite(last) && last > 0 && now - last < RELOAD_WINDOW_MS) return false;
    window.sessionStorage.setItem(RELOAD_KEY, String(now));
    return window.sessionStorage.getItem(RELOAD_KEY) === String(now);
  } catch {
    // Sin sessionStorage no se puede recordar el intento: no se recarga (mejor el error que un bucle)
    return false;
  }
}

/**
 * Si `error` es un chunk que no cargó y aún no se intentó, y la red responde, limpia y recarga.
 * Devuelve `true` si la página se está recargando (quien llama debe dejar de trabajar).
 */
export async function recoverFromStaleChunk(error: unknown, now: () => number = Date.now): Promise<boolean> {
  if (!isChunkLoadError(error)) return false;
  if (!markReload(now())) return false;
  if (!(await networkReachable())) return false;
  await clearAppCaches();
  window.location.reload();
  return true;
}
